/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../../../platform/Platform';
import { ComponentManagerFn } from '../../../../registry/Component.type';
import { baseComponentManager } from '../../../../registry/Components';
import { baseColumnProps } from '../BaseColumn';
import { defaultValue } from './FormulaStringColumn';

const FormulaStringColumnManager: ComponentManagerFn<'FormulaStringColumn'> = (
  platform: Platform,
) => {
  return {
    ...baseComponentManager<'FormulaStringColumn'>(
      platform,
      'FormulaStringColumn',
    ),
    type: 'FormulaStringColumn',
    name: 'Formula String Column',
    isChildAllowed: (page, item, childType) => false,
    defaultValue,
    props: {
      ...baseColumnProps,
      index: {
        label: 'Index',
        type: 'number',
      },
      formula: {
        label: 'Formula',
        type: 'expression',
        returnType: async () => 'string',
      },
    },
    getDisplayText: (item) =>
      typeof item.props.label === 'string'
        ? item.props.label
        : item.props.label.value ?? '',
    category: 'Data Grid',
    icon: { iconName: 'sigma' },
    description: `**Overview**<br/>
The Formula String Column is a read-only Data Grid column that generates text values dynamically based on other data in the same row.
Similar to the Formula Number Column, this component executes a logic expression in the browser to derive its content. It is most commonly used for Concatenation (joining fields together) or Conditional Logic (displaying status text based on numeric thresholds).

**1. Core Logic (The Formula)**<br/>
The functionality of this column is defined entirely by the expression you provide.
* **Formula:** A string expression that runs for every row.
* **Context:** You can access other columns in the specific row using the row context variable (typically \`$._\` or \`$.row\`).
* **Reactivity:** If the source columns change (e.g., a user edits the "First Name"), this column updates immediately.
* **Common Formula Examples:**
    * **Concatenation (Full Name):** \`$._.firstName + ' ' + $._.lastName\`
    * **Address Formatting:** \`$._.city + ', ' + $._.state + ' ' + $._.zip\`
    * **Conditional Status:** \`$._.inventoryCount < 10 ? 'Low Stock' : 'In Stock'\`

**2. Configuration**<br/>
* **Label:** The header text for the column (e.g., "Full Name").
* **Index:** Controls the visual position of the column within the grid order.

**3. Usage Guidelines**<br/>
* **Read-Only:** Since the value is computed, users cannot manually type into this column.
* **Sorting/Filtering:** Because these values are generated on the client side and do not exist in the database, server-side sorting and filtering on this specific column are generally not supported unless the backend has a matching computed column.

**4. Best Use Cases**<br/>
* **Identity:** Displaying "Last Name, First Name" for sorting purposes without storing it that way.
* **Labeling:** Converting complex codes into human-readable text (e.g., transforming SKU-123-RED into "Red Item").
* **Notifications:** Creating simple alert messages in a row based on data state.`,
  };
};

export default FormulaStringColumnManager;
